import { tr } from '@/lib/i18n-core';
import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Check, Plus, Search } from 'lucide-react';
import type { Orderable, OrderablesPage } from '@his/shared';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, money } from '@/lib/utils';
import { Badge, Button, Input, Skeleton } from '@/components/ui/primitives';
import { useDebounced } from '../../../diagnostics/LabBenchDebounce';
import { groupByInitial } from './draft';
import type { OrderKind } from './types';

const PAGE_SIZE = 50;
const CATALOGUE_FRESH_MS = 5 * 60_000;

function hintText(kind: OrderKind, hint: string | null): string | null {
  if (!hint) return null;
  return kind === 'radiology' ? hint.toUpperCase() : hint;
}

function OrderableRow({ kind, row, count, onPick }: { kind: OrderKind; row: Orderable; count: number; onPick: (row: Orderable) => void }) {
  const hint = hintText(kind, row.hint);
  return (
    <button
      type="button"
      onClick={() => onPick(row)}
      aria-label={tr('opd', 'order_add_x', { name: row.name })}
      className={cn(
        'focus-ring flex w-full items-center gap-3 border-b px-3 py-2 text-start text-sm transition-colors last:border-b-0 hover:bg-accent',
        count > 0 && 'bg-primary/[0.04]',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{row.name}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-mono">{row.code}</span>
          {row.labPanelId && <Badge variant="secondary">{tr('opd', 'order_panel')}</Badge>}
          {hint && <span>· {hint}</span>}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className={cn('tabular text-sm', !row.priced && 'text-muted-foreground')}>{row.priced ? money(row.priceMinor) : tr('opd', 'order_no_price')}</span>
        {!row.inHouse && <Badge variant="warning">{tr('opd', 'order_outside_only')}</Badge>}
      </span>
      {count > 0 ? (
        <Badge variant="default" className="shrink-0">
          <Check className="h-3 w-3" aria-hidden />
          <span className="tabular">×{count}</span>
        </Badge>
      ) : (
        <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
    </button>
  );
}

export function OrderablePicker({
  kind,
  quantities,
  onPick,
  initialSearch = '',
}: {
  kind: OrderKind;
  quantities: ReadonlyMap<string, number>;
  onPick: (row: Orderable) => void;
  initialSearch?: string;
}) {
  const [search, setSearch] = useState(initialSearch);
  const term = useDebounced(search.trim(), 300);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const catalogue = useInfiniteQuery({
    queryKey: ['orderables', kind, term],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => get<OrderablesPage>(`/orderables${qs({ type: kind, q: term || undefined, limit: PAGE_SIZE, cursor: pageParam || undefined })}`, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: CATALOGUE_FRESH_MS,
  });
  const rows = catalogue.data?.pages.flatMap((page) => page.data) ?? [];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = catalogue;

  useEffect(() => {
    const target = sentinelRef.current;
    const root = listRef.current;
    if (!target || !root || !hasNextPage || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { root, rootMargin: '120px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          className="ps-9"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={tr('opd', `order_search_${kind}`)}
          aria-label={tr('opd', `order_search_${kind}`)}
        />
      </div>
      <div ref={listRef} className="max-h-80 min-h-40 overflow-y-auto rounded-xl border bg-card" aria-busy={catalogue.isFetching}>
        {catalogue.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : catalogue.error && rows.length === 0 ? (
          <p className="p-3 text-sm text-destructive">{describeError(catalogue.error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">{term ? tr('opd', 'order_catalogue_nothing') : tr('opd', 'order_catalogue_empty')}</p>
        ) : (
          groupByInitial(rows).map((group) => (
            <section key={group.rows[0]?.chargeItemId ?? group.initial} aria-label={group.initial}>
              <h4 className="sticky top-0 z-10 border-b bg-muted/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">{group.initial}</h4>
              {group.rows.map((row) => (
                <OrderableRow key={row.chargeItemId} kind={kind} row={row} count={quantities.get(row.chargeItemId) ?? 0} onPick={onPick} />
              ))}
            </section>
          ))
        )}
        {hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center p-2">
            <Button size="sm" variant="ghost" onClick={() => void fetchNextPage()} loading={isFetchingNextPage}>
              {tr('opd', 'order_load_more')}
            </Button>
          </div>
        )}
        {catalogue.isFetchNextPageError && <p className="p-2 text-center text-xs text-destructive">{describeError(catalogue.error)}</p>}
      </div>
    </div>
  );
}
